import {
  Alert,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Space,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';

import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { FormContainer } from '../../../components/Container';
import { DATE_WITH_DASH } from '../../../constants/time-formats';
import FooterNavigator from '../../../components/FooterNavigator';
import {
  PAYMENT, DOCUMENTS, PHN_ROUTE, PHN_DOCUMENTS, PHN_GP_DETAILS,
} from '../../../constants/routes';
import {
  selectBookingData,
  selectBookingMedicareCardData,
  selectBookingQuestionAndAnswersData,
  updateBookingMedicareCardData,
} from '../../../redux/slices/booking';
import { useAppDispatch } from '../../../hooks/redux-hooks';
import { CARD_TYPES, MedicareCard } from '../../../model/MedicareCard';
import { DVACardType } from '../../../model/DVACardType';
import './index.css';
import analytics from '../../../infra/analytics';
import { QUERIES } from '../../../styles/breakpoint';

const MedicareForm = () => {
  const isMobile = useMediaQuery({ query: QUERIES.mobile });
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const bookingData = useSelector(selectBookingData);

  const medicareCardData = useSelector(selectBookingMedicareCardData);
  const questionAndAnswersData = useSelector(selectBookingQuestionAndAnswersData);
  const dispatch = useAppDispatch();
  const [cardType, setCardType] = useState(CARD_TYPES.MEDICARE);

  const submit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const { expiry, dva, ...otherValues } = values;

      dispatch(updateBookingMedicareCardData({
        ...otherValues,
        dva,
        expiry: expiry ? expiry.endOf('month').format(DATE_WITH_DASH) : undefined,
      }));

      // dispatch(addBookingQuestionAndAnswersData({
      //   ...questionAndAnswersData,
      //   hasDept: dva?.hasDept ? 'YES' : 'NO',
      // }));
      return true;
    } catch (errorInfo) {
      analytics.trackVpcFormError({
        form_id: bookingData.formId,
        error_type: 'form_validation_error',
        page_path: pathname,
      });

      return false;
    }
  }, [form, dispatch, bookingData.formId, pathname]);

  useEffect(() => {
    if (!isEmpty(medicareCardData)) {
      const {
        expiry, dva, cardType: medicareCardType, ...otherData
      } = medicareCardData as MedicareCard;
      form.setFieldsValue({
        ...otherData,
        dva,
        expiry: moment(expiry, DATE_WITH_DASH),
      });
    }
  }, [questionAndAnswersData, medicareCardData, form]);

  const handleCardTypeChange = (e: any) => {
    setCardType(e.target.value);
    form.setFieldsValue({
      cardType: e.target.value,
      medicareCardNumber: undefined,
      medicareCardIRN: undefined,
      expiry: undefined,
      dva: undefined,
    });
  };

  return (
    <FormContainer layout="vertical" form={form}>
      <Form.Item
        name="cardType"
        initialValue={cardType}
      >
        <Radio.Group onChange={handleCardTypeChange} value={cardType}>
          <Space direction="vertical" style={{ fontSize: '16px' }}>
            <Radio value={CARD_TYPES.MEDICARE}>Medicare Card</Radio>
            <Radio value={CARD_TYPES.DVA}>DVA Card</Radio>
            <Radio value={CARD_TYPES.NONE}>{pathname.includes(`${PHN_ROUTE}`) ? 'Patient does not have a Medicare or DVA Card' : "I don't have medicare card or DVA card"}</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>

      {cardType === CARD_TYPES.MEDICARE && (
        <>
          <Form.Item
            label="Medicare Card Number"
            required
            name="medicareCardNumber"
            rules={[
              {
                required: true,
                message: 'Please input your Medicare card number.',
              },
              {
                pattern: /^(\d{10})$/,
                message: 'Please input a valid Medicare card number.',
              },
            ]}
          >
            <Input placeholder="*********" />
          </Form.Item>

          <Form.Item
            label={(
              <>
                Medicare IRN (individual reference number)
                <Tooltip
                  overlayClassName="tooltips-card"
                  placement="right"
                  title={
                    <img alt="IRN example" src={`${process.env.PUBLIC_URL}/IRN.png`} />
                  }
                >
                  <InfoCircleOutlined style={{ marginLeft: '10px' }} />
                </Tooltip>
              </>
            )}
            required
            name="medicareCardIRN"
            rules={[
              { required: true, message: 'Please input your Medicare card IRN.' },
              {
                pattern: /^(\d)$/,
                message: 'Please input a valid Medicare card IRN.',
              },
            ]}
          >
            <Input placeholder="*" />
          </Form.Item>

          <Form.Item
            label="Expiry"
            required
            name="expiry"
            rules={[{ required: true, message: 'Please input your Medicare card number\'s expiry date.' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="MM/YYYY"
              format="MM/YYYY"
              picker="month"
            />
          </Form.Item>
        </>
      )}

      {cardType === CARD_TYPES.DVA && (
        isMobile ? (
          <>
            <Form.Item
              required
              rules={[
                {
                  required: true,
                  message: 'Please input your dva card type.',
                },
              ]}
              name={['dva', 'cardType']}
              label="DVA Card Type"
            >
              <Radio.Group>
                <Radio value={DVACardType.GOLD}>Gold</Radio>
                <Radio value={DVACardType.WHITE}>White</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              style={{ width: '50%' }}
              required
              rules={[
                {
                  required: true,
                  message: 'Please input your dva card number.',
                },
                {
                  pattern: /^[N,V,Q,S,W,T]([A-Z ][0-9]{1,6}|[A-Z]{2}[0-9]{1,5}|[A-Z]{3}[0-9]{1,4})[A-Z]?$/,
                  message: 'Please input a valid DVA card number.',
                },
              ]}
              name={['dva', 'cardNumber']}
              label="DVA No."
            >
              <Input />
            </Form.Item>
          </>
        ) : (
          <Row justify="space-between">
            <Form.Item
              required
              rules={[
                {
                  required: true,
                  message: 'Please input your dva card type.',
                },
              ]}
              name={['dva', 'cardType']}
              label="DVA Card Type"
            >
              <Radio.Group>
                <Radio value={DVACardType.GOLD}>Gold</Radio>
                <Radio value={DVACardType.WHITE}>White</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              style={{ width: '50%' }}
              required
              rules={[
                {
                  required: true,
                  message: 'Please input your dva card number.',
                },
                {
                  pattern: /^[N,V,Q,S,W,T]([A-Z ][0-9]{1,6}|[A-Z]{2}[0-9]{1,5}|[A-Z]{3}[0-9]{1,4})[A-Z]?$/,
                  message: 'Please input a valid DVA card number.',
                },
              ]}
              name={['dva', 'cardNumber']}
              label="DVA No."
            >
              <Input />
            </Form.Item>
          </Row>
        )
      )}

      {cardType === CARD_TYPES.NONE && (
        <Alert
          message={pathname.includes(`${PHN_ROUTE}`)
            ? 'Please inform the patient that if they do not possess a Medicare or DVA card, the Medicare rebate will not be applicable to their healthcare services. As a result, a fully private fee will apply to all services provided. We appreciate your understanding and cooperation.'
            : 'Please be advised that if you do not possess a Medicare or DVA card, the Medicare rebate will not be applicable to your healthcare services. As a result, a fully private fee will apply to all services provided. We appreciate your understanding and cooperation.'}
          type="warning"
          showIcon
          style={{ marginBottom: 24, fontSize: isMobile ? '12px' : '16px' }}
        />
      )}

      <FooterNavigator
        error=""
        submit={submit}
        next={pathname.includes(`${PHN_ROUTE}`) ? PHN_GP_DETAILS : PAYMENT}
        back={pathname.includes(`${PHN_ROUTE}`) ? PHN_DOCUMENTS : DOCUMENTS}
      />
    </FormContainer>
  );
};

export default MedicareForm;
